import {
  PathsV1UserSearchPostParametersQueryOrderByDirection,
  PathsV1UserSearchPostParametersQueryOrderByField,
  UserStatus,
  UserType,
} from "@/api-schema-enums";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { useUserByUuidQuery } from "@/common/api/user/use-user-by-uuid.query";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { EmptyValue } from "@/components/ui/empty-value";
import { keepPreviousData } from "@tanstack/react-query";
import { MailIcon, PhoneIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface BranchDunningUserSelectProps {
  id?: string;
  value?: string;
  onChange: (uuid: string) => void;
}

function toOption(user: { uuid: string; firstName?: string | null; lastName?: string | null }) {
  return {
    value: user.uuid,
    label: getFullUserName({ user }),
  };
}

export function BranchDunningUserSelect({ id, value, onChange }: BranchDunningUserSelectProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const { searchTerm, debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination();

  const { data: usersData, isPending: isUsersLoading } = useUserSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        orderBy: [
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.LastName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.FirstName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
        ],
      },
      body: {
        status: [UserStatus.Enabled],
        type: [UserType.Employee],
      },
    },
    queryOptions: { placeholderData: keepPreviousData },
  });

  const { data: selectedUser } = useUserByUuidQuery(value ?? "");

  const optionsFromSearch: ComboboxOption[] = useMemo(() => (usersData?.data ?? []).map(toOption), [usersData?.data]);

  const options: ComboboxOption[] = useMemo(() => {
    if (!selectedUser?.uuid || optionsFromSearch.some((option) => option.value === selectedUser.uuid)) {
      return optionsFromSearch;
    }
    return [toOption(selectedUser), ...optionsFromSearch];
  }, [optionsFromSearch, selectedUser]);

  const paginationState = createPaginationState(usersData?.meta?.total, optionsFromSearch.length, isUsersLoading);

  const phone = selectedUser?.telephone || selectedUser?.mobile;

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        id={id}
        options={options}
        value={value}
        onChange={(uuid) => onChange(uuid as string)}
        showClearOption={false}
        searchValue={searchTerm}
        shouldFilter={false}
        isLoading={paginationState.isLoading && options.length === 0}
        hasMore={paginationState.hasMore}
        remainingCount={paginationState.remainingCount}
        isLoadingMore={paginationState.isLoadingMore}
        onLoadMore={paginationState.onLoadMore}
        onSearchChange={paginationState.onSearchChange}
        placeholder={t("common:SelectUser")}
        searchPlaceholder={t("common:SearchUser")}
        emptyText={t("masterdata:BranchDunningNoUsersFound")}
      />

      {value && selectedUser && (
        <div className="text-muted-foreground flex flex-col gap-1 text-sm">
          <div className="flex items-center gap-2">
            <MailIcon className="h-4 w-4 shrink-0" />
            {selectedUser.email || <EmptyValue />}
          </div>
          <div className="flex items-center gap-2">
            <PhoneIcon className="h-4 w-4 shrink-0" />
            {phone || <span className="text-xs">{t("masterdata:BranchDunningUserHasNoPhone")}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
